// CalendarProvider — provider-agnostic interface every calendar back-end
// (Google, Microsoft Graph, CalDAV, iCloud-via-CalDAV) implements.
//
// The sync engine (calendar-sync.js) talks to providers exclusively through
// this surface, so it can fan out across N providers without knowing what
// any of them actually is. Implementations live alongside this file in
// web/js/schedule/providers/.
//
// Lifecycle
//   const p = new GoogleProvider({ accountId, credentialKey });
//   await p.init();                       // hydrate creds from the vault
//   const cals = await p.listCalendars(); // [{id, title, color, writable}]
//   const { events, nextSyncToken } = await p.fetchEvents({...});
//   const { providerId, etag } = await p.createEvent(calId, evt);
//   await p.updateEvent(calId, providerId, evt, { etag });
//   await p.deleteEvent(calId, providerId, { etag });
//   await p.close();
//
// Event shape (normalised across all providers)
//   { providerId,        — opaque provider-specific id
//     accountId,         — which account this came from (registry key)
//     calendarId,        — which calendar within the account
//     title,
//     startDate, endDate,— ISO 8601 strings (UTC where the provider returns it)
//     allDay,
//     rrule?,            — RFC 5545 RRULE string, no leading "RRULE:"
//     recurrenceId?,     — ISO 8601 of the master occurrence this overrides
//     masterProviderId?, — providerId of the master event when this is an override
//     etag, sequence,    — concurrency tokens for If-Match writes
//     lastModified,      — ISO 8601, used to break sequence ties
//     calendarTitle,     — human-readable, used for @CalendarName routing
//     deleted? }         — true for tombstones returned by delta/sync queries
//
// Error shape (single class, code enum)
//   throw new CalendarProviderError(CODE, 'human readable', { cause })
//   Codes:
//     AUTH_REQUIRED       — 401: token expired / revoked, refresh failed
//     FORBIDDEN           — 403: permission denied; a token refresh won't help
//     RATE_LIMITED        — provider asked us to back off; .retryAfter set
//     CONFLICT            — write rejected, server state advanced
//     NOT_FOUND           — resource gone (often safe to ignore on delete)
//     OFFLINE             — network unreachable; orchestrator enqueues
//     PRECONDITION_FAILED — 412 If-Match mismatch; orchestrator re-fetches
//     TRANSIENT           — 5xx or transient client error; orchestrator retries
//     UNSUPPORTED         — operation not available for this provider
//
// The actual classes (Google/Microsoft/CalDAV/iCloud providers) are loaded
// separately and registered with provider-registry.js. This module just
// defines the shapes they all share.

// ── Error class ───────────────────────────────────────────────────────────
export class CalendarProviderError extends Error {
  [key: string]: any;
  /**
   * @param {string} code - one of ERROR_CODES
   * @param {string} [message]
   * @param {{ cause?: unknown, retryAfter?: number, status?: number }} [opts]
   */
  constructor(code: any, message: any, opts?: any) {
    super(message || code);
    this.name = 'CalendarProviderError';
    this.code = code;
    if (opts?.cause) this.cause = opts.cause;
    if (opts && typeof opts.retryAfter === 'number') this.retryAfter = opts.retryAfter;
    if (opts?.status) this.status = opts.status;
  }
}

export const ERROR_CODES = Object.freeze({
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  FORBIDDEN: 'FORBIDDEN',
  RATE_LIMITED: 'RATE_LIMITED',
  CONFLICT: 'CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  OFFLINE: 'OFFLINE',
  PRECONDITION_FAILED: 'PRECONDITION_FAILED',
  TRANSIENT: 'TRANSIENT',
  UNSUPPORTED: 'UNSUPPORTED',
});

// ── Capability flags ────────────────────────────────────────────────────
// Providers advertise what they support so the orchestrator can avoid
// calling methods that would throw UNSUPPORTED. Defaults are conservative.
export const DEFAULT_CAPABILITIES = Object.freeze({
  rrule: false,
  delta: false, // incremental sync via syncToken / @odata.deltaLink
  push: false, // server-pushed change notifications
  etag: false, // If-Match-style optimistic concurrency
  freeBusy: false,
  batch: false, // batched create/update/delete calls
});

// ── Base class ──────────────────────────────────────────────────────────
// Subclasses override the methods marked `// abstract`. The defaults throw
// UNSUPPORTED so callers never silently no-op when a subclass forgets a
// method.
export class CalendarProvider {
  [key: string]: any;
  /** @param {any} [opts] - { id, accountId, credentialKey, label, serverUrl, capabilities } */
  constructor(opts: any) {
    const o = opts || {};
    // `id` identifies the provider kind: 'google', 'microsoft',
    // 'caldav', 'icloud'. `accountId` identifies one set of credentials
    // (e.g. 'google:alice@x.com'). Two Google accounts have the same
    // `id` and different `accountId`s.
    this.id = o.id || 'unknown';
    this.accountId = o.accountId || null;
    this.credentialKey = o.credentialKey || null; // workspace-session-vault key
    this.label = o.label || this.accountId || this.id;
    this.serverUrl = o.serverUrl || null;
    this.capabilities = Object.freeze({ ...DEFAULT_CAPABILITIES, ...(o.capabilities || {}) });
    this._initialised = false;
  }

  // ── Lifecycle ──
  /** @returns {Promise<void>} */
  async init(): Promise<any> {
    this._initialised = true;
  } // override to load credentials
  /** @returns {Promise<void>} */
  async close(): Promise<any> {
    this._initialised = false;
  }

  // ── Calendar list ──
  // → Promise<[{id, title, color?, writable: boolean, defaultCalendar?: boolean}]>
  /** @returns {Promise<any[]>} */
  async listCalendars(): Promise<any> {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'listCalendars not implemented');
  }

  // ── Event fetch ──
  // opts: { calendarId, startDate (ISO), endDate (ISO), syncToken? }
  // → Promise<{ events: NormalisedEvent[], nextSyncToken?: string, deletedIds?: string[] }>
  /**
   * @param {any} _opts - { calendarId, startDate, endDate, syncToken? }
   * @returns {Promise<any>}
   */
  async fetchEvents(_opts: any): Promise<any> {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'fetchEvents not implemented');
  }

  // ── Writes ──
  // create → { providerId, etag?, sequence?, lastModified? }
  /**
   * @param {string} _calendarId
   * @param {any} _event
   * @returns {Promise<any>}
   */
  async createEvent(_calendarId: any, _event: any): Promise<any> {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'createEvent not implemented');
  }

  /**
   * @param {string} _calendarId
   * @param {string} _providerId
   * @param {any} _event
   * @param {any} [_opts]
   * @returns {Promise<any>}
   */
  async updateEvent(_calendarId: any, _providerId: any, _event: any, _opts: any): Promise<any> {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'updateEvent not implemented');
  }

  /**
   * @param {string} _calendarId
   * @param {string} _providerId
   * @param {any} [_opts]
   * @returns {Promise<any>}
   */
  async deleteEvent(_calendarId: any, _providerId: any, _opts: any): Promise<any> {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'deleteEvent not implemented');
  }

  // ── Recurrence ──
  // Some providers (Google, MS) expand occurrences server-side when
  // `singleEvents=true`; CalDAV does not. Providers with `capabilities.rrule`
  // return master + RRULE; the orchestrator materialises occurrences via
  // ical-codec's expandRrule() in the schedule view (no per-provider hook).

  // ── Change watcher ──
  // Long-poll / WebSocket / Capacitor listener. Returns an unsubscribe
  // function. Default no-op so providers without push fall back to the
  // orchestrator's 5-min poll.
  /**
   * @param {string[]} _calendarIds
   * @param {Function} _onChange
   * @returns {() => void} an unsubscribe function.
   */
  watch(_calendarIds: any, _onChange: any): any {
    return () => {};
  }
}
